import { useCallback, useEffect, useRef } from 'react';
import { FieldValues, get, Path, useController, useWatch } from 'react-hook-form';

interface UseFieldDependencyParams<FormValues, D> {
    dependency: D;
    name: Path<FormValues>;
    setInitialValueDisabled?: boolean;
}

export const useFieldDependency = <FormValues extends FieldValues, D extends Path<FormValues>>({
    dependency,
    name,
    setInitialValueDisabled,
}: UseFieldDependencyParams<FormValues, D>) => {
    const {
        field: { value, onChange: controllerOnChange },
        formState: { defaultValues },
    } = useController<FormValues>({ name });
    const initialValue = get(defaultValues, name);

    const currentDependencyValue = useWatch<FormValues, D>({ name: dependency });
    const dependencyValue = useRef(currentDependencyValue);

    const setInitialValue = useCallback(() => {
        if (value !== initialValue) {
            controllerOnChange(initialValue);
        }
    }, [initialValue, value, controllerOnChange]);

    // set initialValue on dependency value change
    useEffect(() => {
        if (dependencyValue.current !== currentDependencyValue) {
            dependencyValue.current = currentDependencyValue;

            if (setInitialValueDisabled) return;

            setInitialValue();
        }
    }, [currentDependencyValue, setInitialValue, setInitialValueDisabled]);

    return { disabled: currentDependencyValue == null, dependencyValue: currentDependencyValue, value };
};
